import { useId, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  apiErrorStatus,
  apiErrorText,
  useCreatePrcVersionMutation,
  type PrcVersionSummary,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Select } from '@shared/ui/Input';
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerFooter,
  DrawerHeader,
} from '@shared/ui/Drawer';
import { useActiveProjectContext } from '@shared/lib/activeProjectContext';
import { typo } from '@entities/fleet';
import {
  defaultObjectIds,
  formatIsoDate,
  localIsoDate,
  prcStatusLabel,
  takenObjects,
  versionObjects,
} from '@entities/prc';
import { Control, ControlArea } from '@widgets/FleetRegistry/formFields';
import { Actions, FooterStatus, Notice } from '@widgets/FleetRegistry/ui';
import { describedBy, DrawerTitle, Eyebrow, FormField, HeaderRow, Titles } from '../drawerParts';
import { formatDay } from '../format';
import { ObjectsField } from '../ObjectsField';
import { Lead, SchemeScope } from '../ui';

/**
 * Последняя утверждённая — её копирует черновик без явной основы. Как на
 * сервере: сначала версия первого объекта расчёта, потом общая, потом любая.
 */
function latestApproved(
  versions: readonly PrcVersionSummary[],
  projectId: string | undefined,
): PrcVersionSummary | null {
  const rank = (v: PrcVersionSummary) =>
    projectId && versionObjects(v).some((o) => o.id === projectId)
      ? 0
      : v.objects.length === 0
        ? 1
        : 2;
  let best: PrcVersionSummary | null = null;
  for (const v of versions) {
    if (v.status !== 'approved') continue;
    if (
      !best ||
      rank(v) < rank(best) ||
      (rank(v) === rank(best) && (v.approved_at ?? '') > (best.approved_at ?? ''))
    ) {
      best = v;
    }
  }
  return best;
}

/**
 * «Новый расчёт»: черновик-копия последней утверждённой или выбранной версии.
 * Модели берутся из текущих паспортов справочника техники, пересчёт — сразу.
 */
export function NewVersionDrawer({
  versions,
  baseVersionId,
  onClose,
  onCreated,
}: {
  versions: readonly PrcVersionSummary[];
  /** Основа по умолчанию — «Создать копию» из версии. */
  baseVersionId?: string;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const uid = useId();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const { activeProjectId } = useActiveProjectContext();
  const base = versions.find((v) => v.id === baseVersionId) ?? null;
  // Основа с вариантами условий — регионы в одной калькуляции: копия по
  // умолчанию сохраняет её устройство (объекты основного расчёта; объекты
  // вариантов уходят в копию вместе с вариантами). Иначе объект шапки стал бы
  // основным расчётом копии — с условиями чужого района.
  const [objectIds, setObjectIds] = useState<string[]>(() => {
    const regional = base ?? latestApproved(versions, activeProjectId || undefined);
    return regional && (regional.variants ?? []).length > 0
      ? regional.objects.map((o) => o.id)
      : defaultObjectIds(activeProjectId, base?.objects);
  });
  const approved = useMemo(() => latestApproved(versions, objectIds[0]), [versions, objectIds]);
  const [label, setLabel] = useState(() =>
    base ? `Копия — ${base.label}` : `Расчёт ПРЦ от ${formatIsoDate(localIsoDate(new Date()))}`,
  );
  const [baseId, setBaseId] = useState(baseVersionId ?? '');
  const [comment, setComment] = useState('');
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState<{ text: string; registry: boolean } | null>(null);
  const [create, { isLoading }] = useCreatePrcVersionMutation();

  const labelError =
    label.trim() === '' ? 'Назовите расчёт — так его найдут в списке версий' : null;
  // Варианты основы переходят в копию со своими объектами.
  const chosenBase = baseId ? versions.find((v) => v.id === baseId) : approved;
  const baseVariants = chosenBase?.variants ?? [];
  const variantObjects = useMemo(() => takenObjects([], baseVariants, null), [baseVariants]);
  const defaultBase = approved
    ? `Действующая версия «${approved.label}»`
    : 'Блоки книги ЭММ, загруженной в справочник техники';

  async function submit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    setError(null);
    if (labelError) return;
    try {
      const res = await create({
        label: label.trim(),
        base_version_id: baseId || undefined,
        comment: comment.trim() || undefined,
        project_ids: objectIds,
      }).unwrap();
      onCreated(res.version.id);
    } catch (err) {
      setError({
        text: apiErrorText(err, 'Не удалось создать расчёт.'),
        registry: apiErrorStatus(err) === 409,
      });
    }
  }

  const ids = {
    label: `${uid}-label`,
    base: `${uid}-base`,
    objects: `${uid}-objects`,
    comment: `${uid}-comment`,
  };
  const showLabelError = touched && labelError !== null;

  return (
    <Drawer open onClose={onClose} labelledBy={`${uid}-title`} initialFocusRef={titleRef}>
      <SchemeScope>
        <DrawerHeader>
          <HeaderRow>
            <Titles>
              <Eyebrow>ПРЦ машино-часа · новая версия</Eyebrow>
              <DrawerTitle id={`${uid}-title`} ref={titleRef} tabIndex={-1}>
                Новый расчёт
              </DrawerTitle>
            </Titles>
            <DrawerCloseButton onClick={onClose} />
          </HeaderRow>
        </DrawerHeader>
        <DrawerBody>
          <Lead>
            {typo(
              'Черновик копирует цены, ФОТ механизаторов, параметры, перечни лимитов и варианты условий основы. Модели — текущие паспорта справочника техники. ОСНОВА пересчитает все модели сразу; дальше меняйте цены и смотрите «было → стало».',
            )}
          </Lead>
          {error && (
            <Notice $tone="danger" role="alert">
              <span className="text">{error.text}</span>
              {error.registry && <Link to="/agents/econ/fleet">Открыть «Технику»</Link>}
            </Notice>
          )}
          <form id={`${uid}-form`} onSubmit={submit} noValidate>
            <FormField
              id={ids.label}
              label="Название"
              required
              error={showLabelError ? labelError : null}
            >
              <Control
                id={ids.label}
                value={label}
                autoComplete="off"
                aria-required="true"
                aria-invalid={showLabelError ? true : undefined}
                aria-describedby={describedBy(ids.label, false, showLabelError)}
                onChange={(e) => setLabel(e.target.value)}
                onBlur={() => setTouched(true)}
              />
            </FormField>
            <FormField
              id={ids.base}
              label="Основа"
              hint={typo(
                baseId
                  ? 'Копия выбранной версии: её цены, ФОТ, параметры и перечни лимитов.'
                  : approved
                    ? `Последняя утверждённая версия, утверждена ${formatDay(approved.approved_at)}.`
                    : 'Утверждённых версий ещё нет — основа берётся из книги ЭММ, загруженной в справочник техники.',
              )}
            >
              <Select
                id={ids.base}
                value={baseId}
                aria-describedby={`${ids.base}-hint`}
                onChange={(e) => setBaseId(e.target.value)}
              >
                <option value="">{defaultBase}</option>
                {versions.length > 0 && (
                  <optgroup label="Копия другой версии">
                    {versions.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.label} — {prcStatusLabel(v.status).toLowerCase()}
                      </option>
                    ))}
                  </optgroup>
                )}
              </Select>
            </FormField>
            <ObjectsField
              id={ids.objects}
              value={objectIds}
              onChange={setObjectIds}
              taken={variantObjects}
              hint={
                baseVariants.length > 0
                  ? `У основы ${baseVariants.length === 1 ? 'есть вариант условий' : 'есть варианты условий'} ${baseVariants.map((x) => `«${x.name}»`).join(', ')} — ${baseVariants.length === 1 ? 'он перейдёт' : 'они перейдут'} в копию со своими объектами. Здесь — объекты основного расчёта копии.`
                  : undefined
              }
            />
            <FormField id={ids.comment} label="Комментарий">
              <ControlArea
                id={ids.comment}
                value={comment}
                rows={3}
                placeholder="Например: тюменские цены на топливо и ФОТ"
                onChange={(e) => setComment(e.target.value)}
              />
            </FormField>
          </form>
        </DrawerBody>
        <DrawerFooter>
          <FooterStatus role="status">{isLoading ? 'Создаю и пересчитываю…' : ''}</FooterStatus>
          <Actions>
            <Button type="button" $variant="ghost" onClick={onClose} disabled={isLoading}>
              Отмена
            </Button>
            <Button type="submit" form={`${uid}-form`} $variant="primary" disabled={isLoading}>
              {isLoading ? 'Создаю…' : 'Создать черновик'}
            </Button>
          </Actions>
        </DrawerFooter>
      </SchemeScope>
    </Drawer>
  );
}
